/**
 * Wordings of «Учитель» v3 — family «lessons» (docs/TEACHING-V3.md §5; pool specs: ../spec.ts).
 * Rules: no Latin, NO squares (the board shows them), Гамбитик speaks of himself as a boy, the child through {g:…},
 * no «молодец», no clock words; «вот эти клетки / этот фланг / сюда» only in pools with a board cue.
 *
 * Mini-lessons `v3.mini.<topic>.l1|l2|l3` (docs/TEACHING-V3.md §2.6; topics: MINI_TOPICS in ../spec.ts): l1 — what it
 * is, l2 — why and how, l3 — an exception or a trap; each level says something new, 2–3 short sentences. A lesson is a
 * general rule (it never claims anything about the position that its trigger does not prove), no piece placeholders.
 * Blitz takes only ONE-sentence wordings (docs/TEACHING-V3.md §2.2: in blitz a mini is «как думать» or a lesson inside a
 * danger): `thinking` keeps ≥ 3 one-sentence wordings per level and band, the danger topics (hanging, checkEscape, fork,
 * mateInOne, scholarsMate, backRank) ≥ 2 — marked «one sentence (blitz)»; no «? ! .» inside them. A danger lesson is
 * said BEFORE the move that answers the danger: no «отбили / не удался» as if it were already answered.
 * Topics whose trigger can be either side's (hanging, fork, earlyQueen, mateInOne) are worded so they stay true whether
 * the piece in question is ours or the opponent's: «соперник» never stands for the side that suffers.
 * Banded topics (their range spans stages 1–2 and 3–5) have their own simpler words for the young band. A stage term
 * (docs/TEACHING-V3.md §2.10) appears only in wordings tagged from its stage on: «детский мат», «вилка», «пат» from 2;
 * «связка», «диагональ», «вертикаль», «фланг», «открытая линия» from 3; «защитник», «вскрытое», «отвлечь» from 4;
 * «проходная», «эндшпиль», «активный король» from 5.
 */
import type { LessonStage, LessonWording, LessonWordings } from '../types.ts';

const at =
  (a: LessonStage, b: LessonStage) =>
  (t: string): LessonWording => ({ t, stages: [a, b] });
/** the young band of a topic that starts at stage 1 */
const s12 = at(1, 2);
/** the young band of a topic that starts at stage 2 */
const s2 = at(2, 2);
const s3 = at(3, 3);
const s34 = at(3, 4);
const s35 = at(3, 5);
const s45 = at(4, 5);
const s5 = at(5, 5);
/** the whole range of the pool */
const all = (t: string): LessonWording => ({ t });

export const WORDINGS: LessonWordings = {
  // ───────────────────────── castle [1–5], cue king ─────────────────────────
  'v3.mini.castle.l1': [
    s12('Рокировка — особый ход: король прячется в домик, а ладья выходит из угла.'),
    s12('Король не любит стоять посередине. Рокировкой он прячется за пешки, как в домик. А ладья заодно выходит на работу.'),
    s12('Знаешь, чем хороша рокировка? Король уходит в тихий уголок, а ладья просыпается. И всё это одним ходом!'),
    s12('Король шагает на две клетки к ладье, а ладья перепрыгивает через него. Так устроена рокировка.'),
    s35('Рокировка решает две задачи сразу. Король уходит в укрытие за пешки, а ладья встаёт ближе к центру.'),
    s35('Только в рокировке ходят сразу две фигуры. Король прячется на фланге, а ладья покидает угол.'),
    s35('В дебюте две заботы: вывести фигуры и спрятать короля. Рокировка прячет короля и заодно будит ладью.'),
    s35('Короля стоит увести из центра пораньше. Рокировка прячет его за стенку пешек и выводит ладью.'),
  ],
  'v3.mini.castle.l2': [
    s12('Почему королю опасно в центре? Там пешки быстро уходят, и открываются дороги. По ним к королю прибегут чужие фигуры.'),
    s12('Посередине доски скоро начнутся размены. Пешки уйдут, и король останется на ветру. Поэтому его прячут заранее.'),
    s12('Король в центре — как дом с открытыми окнами. Чужие ладьи и ферзь легко до него доберутся. А за стенкой пешек ему спокойно.'),
    s12('Пока король в центре, ему нужна охрана. А после рокировки его сторожат пешки, и фигуры свободны для игры.'),
    s35('В центре пешки быстро меняются, и открываются линии. По ним ладьи и ферзь соперника доберутся до короля.'),
    s35('Пока в центре стоят пешки, король там живёт спокойно. Но стоит им разменяться — и открытые вертикали ведут прямо к нему.'),
    s35('Атака на короля в центре начинается быстро. Одна открытая линия — и шахи сыплются один за другим.'),
    s35('Король в центре мешает и своим: ладьи не видят друг друга. После рокировки они соединяются и вступают в бой.'),
  ],
  'v3.mini.castle.l3': [
    s12('У рокировки есть правила. Нельзя делать её, когда королю шах. И нельзя, если король уже ходил.'),
    s12('Рокироваться нельзя через клетку, по которой стреляет чужая фигура. Сначала закрой ей дорогу или прогони её.'),
    s12('После рокировки пешки перед королём — его стенка. Лишний раз их не двигай, иначе в стенке появятся дырки.'),
    s12('Секрет: если король хоть раз сходил, рокировки уже не будет. Поэтому до рокировки короля не трогаем.'),
    s35('Рокировка запрещена, если королю шах или он уже ходил. И нельзя проходить через клетку, которую бьёт соперник.'),
    s35('После рокировки не спеши двигать пешки перед королём. Каждый такой шаг — дырка в стене, куда полезут фигуры соперника.'),
    s35('Если ладья уже ходила, в её сторону рокироваться нельзя. Береги право на рокировку, пока король в центре.'),
    s35('Бывает, соперник мешает рокировке: его фигура бьёт клетку на пути короля. Закрой ей дорогу или прогони — и прячь короля.'),
  ],

  // ───────────────────────── development [1–5], cue piece ─────────────────────────
  'v3.mini.development.l1': [
    s12('В начале партии фигуры спят дома. Каждым ходом будим новую фигуру — коня или слона.'),
    s12('Хочешь сильную армию? Выводи в игру новую фигуру каждым ходом. Пусть проснутся все!'),
    s12('Кони и слоны не должны скучать дома. Один ход — одна новая фигура в игре.'),
    s12('Посмотри, кто из наших фигур ещё сидит дома. Каждым ходом выпускаем одну новую — как игроков на поле.'),
    s35('Развитие — значит вывести фигуры из дома в игру. В дебюте стараемся каждым ходом выводить новую.'),
    s35('Главное в дебюте — быстрое развитие. Один ход — одна новая фигура, пока не выйдут кони и слоны.'),
    s35('Кто быстрее выведет фигуры, тот первым начнёт атаку. Поэтому в дебюте каждый ход — новая фигура.'),
    s35('Считай фигуры, которые ещё стоят дома. С каждым нашим ходом их должно становиться меньше.'),
  ],
  'v3.mini.development.l2': [
    s12('Если ходить одной фигурой взад-вперёд, остальные так и спят. Лучше разбудить новую, чем гонять старую.'),
    s12('Одна фигура в бою мало что сделает. Когда вышли все, они помогают друг другу.'),
    s12('В начале партии одной фигурой дважды лучше не ходить. Пока она гуляет, другие ждут дома.'),
    s12('Представь: один конь бегает по доске, а остальные спят. Соперник тем временем выводит всех. У кого армия сильнее?'),
    s35('Ходить одной фигурой дважды в дебюте — терять ход. Соперник за это время выведет ещё одну фигуру.'),
    s35('Лишний ход одной и той же фигурой соперник сразу использует. Он выводит новую фигуру, а мы стоим на месте.'),
    s35('Фигуры сильны вместе: они защищают друг друга и нападают дружно. Поэтому сначала выводим всех, потом атакуем.'),
    s35('Атака одной фигурой редко удаётся. Соперник прогонит её и заодно выведет свою.'),
  ],
  'v3.mini.development.l3': [
    s12('Коня обычно выводят раньше слона. Куда встанет конь, видно сразу, а слону можно и подождать.'),
    s12('Ладьи выходят последними. Сначала кони и слоны, потом рокировка — и только тогда ладьи вступают в игру.'),
    s12('Не все фигуры выходят сразу. Ферзь и ладьи ждут, пока выйдут кони и слоны и спрячется король.'),
    s12('Коня на край доски лучше не ставить. Там у него мало прыжков, и он почти не помогает.'),
    s35('Есть порядок выхода: сначала кони, за ними слоны. Ферзь подождёт, а ладьи оживают после рокировки.'),
    s35('Фигуру, которую легко прогнать пешкой, рано выводить не стоит. Она отступит, и ход пропадёт.'),
    s35('Развитие — это не просто выйти из дома, а встать на хорошее место. Фигура на краю доски почти не работает.'),
    s35('Лишние ходы пешками в дебюте — ловушка. Пока двигаются пешки, фигуры стоят дома.'),
  ],

  // ───────────────────────── center [1–3], cue center ─────────────────────────
  'v3.mini.center.l1': [
    s12('Центр — это четыре клетки посередине доски. Через них проходят главные дороги.'),
    s12('Вот эти четыре клетки — сердце доски. Их называют центром.'),
    s12('Посмотри на середину доски: там четыре главные клетки. Это центр, и за него спорят с первого хода.'),
    s12('Середина доски — как площадь в городе: там сходятся все дороги. Кто там хозяин, тому легко играть.'),
    s3('Четыре клетки посередине доски — центр, перекрёсток главных линий. Борьба за него начинается с первого хода.'),
    s3('Центр — вот эти четыре клетки. Через них идут обе длинные диагонали.'),
    s3('Кто владеет центром, тот владеет доской. Поэтому дебют почти всегда начинается борьбой за центр.'),
  ],
  'v3.mini.center.l2': [
    s12('Из центра фигуры стреляют дальше. Конь в центре смотрит на восемь клеток, а в углу — только на две.'),
    s12('Центр близко к любому углу доски. Фигура оттуда быстро прибежит туда, где она нужнее.'),
    s12('Наши пешки в центре мешают фигурам соперника. Чужим коням и слонам труднее найти хорошее место.'),
    s12('Фигура в центре — как на вершине горки: оттуда видно во все стороны. А из угла много не увидишь.'),
    s3('Из центра фигуры держат больше клеток. И быстро перебегают на любой фланг — туда, где нужнее.'),
    s3('Пешки в центре отнимают хорошие клетки у фигур соперника. Им становится тесно.'),
    s3('Конь в центре бьёт восемь клеток, а в углу — две. Центр делает фигуры сильнее.'),
  ],
  'v3.mini.center.l3': [
    s12('Центр держат не только пешки. Конь или слон, который смотрит на центр, тоже его охраняет.'),
    s12('Секрет: в центре не обязательно стоять. Можно держать его издалека — слоном или конём.'),
    s12('Пешка в центре без помощи может пропасть. Пусть фигуры тоже смотрят на центр и охраняют её.'),
    s12('Не спеши ставить в центр все пешки подряд. Важно, чтобы и фигуры на него смотрели.'),
    s3('Центр можно держать и на расстоянии: слоном с длинной диагонали или конём.'),
    s3('Пешки в центре сильны, только когда им помогают фигуры. Иначе соперник собьёт их своими пешками.'),
    s3('Считай, кто сильнее в центре: сколько наших фигур смотрит туда и сколько чужих.'),
  ],

  // ───────────────────────── earlyQueen [1–4], cue piece ─────────────────────────
  // the trigger is usually the OPPONENT's early queen: «соперник» never stands for the side that chases it
  'v3.mini.earlyQueen.l1': [
    s12('Ферзь сильнее всех на доске. Его берегут и в бой первым не посылают.'),
    s12('Девять пешек — вот сколько стоит ферзь! Такую силу берегут и выводят не спеша.'),
    s12('Ферзь — главный силач армии. Потерять его очень обидно. Поэтому ферзя берегут.'),
    s12('Знаешь, почему ферзь такой сильный? Он ходит и как ладья, и как слон. Такую фигуру берегут.'),
    s34('У ферзя огромная сила: он ходит и по линиям, и по диагоналям. Его цена — девять пешек.'),
    s34('Ферзя берегут: отдать его за коня или слона — большая потеря. Поэтому в дебюте он не спешит в бой.'),
    s34('На ферзя всегда рады напасть: даже пешка может его прогнать. Поэтому ферзь выходит, когда это безопасно.'),
  ],
  'v3.mini.earlyQueen.l2': [
    s12('Если ферзь выходит рано, на него нападают кони и слоны. Ферзь убегает, а нападающие тем временем выходят в игру.'),
    s12('Рано вышедшего ферзя гоняют по всей доске. Он всё убегает, а остальные фигуры его армии так и спят дома.'),
    s12('Ферзю опасно стоять под ударом любой фигуры, даже пешки. Поэтому его легко прогнать и заодно вывести свою фигуру.'),
    s34('Ранний ферзь становится мишенью. На него нападают и с каждым ходом выводят новую фигуру.'),
    s34('Пока ферзь убегает, его армия стоит дома. Так легко отстать в развитии на две-три фигуры.'),
    s34('Кто нападает на раннего ферзя, тот выигрывает ходы. Он развивается, а ферзь бегает.'),
  ],
  'v3.mini.earlyQueen.l3': [
    s12('Ферзь соперника вышел рано? Это наш шанс. Нападай на него новыми фигурами: он убегает, а мы выводим армию.'),
    s12('Прогоняй чужого ферзя конём или слоном. Так мы и фигуры выводим, и ферзя гоняем.'),
    s12('Нападай на ферзя так, чтобы твоя фигура стояла в безопасности. Тогда соперник тратит ход, а мы — нет.'),
    s34('Против раннего ферзя выигрывай ходы: каждый ход — новая фигура и нападение на ферзя.'),
    s34('Не гоняйся за ферзём одной и той же фигурой. Нападай на него каждый раз новой — так вся армия выйдет в игру.'),
    s34('Рано вышедший ферзь соперника — удобная цель. На него может напасть каждый наш новый конь или слон. Мы развиваемся, а он убегает.'),
  ],

  // ───────────────────────── thinking [1–5], no cue ─────────────────────────
  'v3.mini.thinking.l1': [
    s12('Перед каждым ходом задай себе вопрос: что хочет соперник? Посмотри, куда он сходил и зачем.'),
    s12('Мастера сначала смотрят на ход соперника. Зачем он так пошёл? Что он задумал?'),
    s12('Прежде чем ходить — стоп! Посмотри, что сделал соперник и чего он хочет.'),
    s12('Сначала поймём соперника, потом придумаем свой ход. Какую хитрость он готовит?'),
    s12('Шахматист как сыщик. Первым делом он ищет, что задумал соперник.'),
    // one sentence (blitz)
    s12('Перед ходом спроси себя: что задумал соперник?'),
    s12('Глаза на доску: куда сходил соперник и чего он хочет?'),
    s12('У каждого хода соперника есть секрет — попробуй его разгадать!'),
    s12('Сначала разгадай ход соперника, а потом придумывай свой.'),
    s35('Перед ходом первым делом спроси: что хочет соперник? За каждым его ходом прячется идея.'),
    s35('Сильные игроки сначала разгадывают замысел соперника. На что теперь нападает его фигура? Куда она смотрит?'),
    s35('Каждый ход соперника — загадка. Разгадай её, прежде чем думать о своём плане.'),
    s35('Не спеши с ответом. Сначала пойми, зачем соперник сделал свой ход.'),
    s35('Главный вопрос перед ходом: что изменилось после хода соперника? Может, он на кого-то напал?'),
    // one sentence (blitz)
    s35('Первый вопрос перед каждым ходом: чего добивается соперник?'),
    s35('Прежде чем искать свой ход, пойми замысел соперника.'),
    s35('Любой ход соперника что-то меняет на доске — найди, что именно.'),
    s35('Сильный игрок сначала угадывает план соперника и только потом ищет свой ход.'),
  ],
  'v3.mini.thinking.l2': [
    s12('Второй вопрос: мой ход безопасен? Посмотри, не съедят ли фигуру там, куда ты её ставишь.'),
    s12('Выбираешь ход? Проверь, не окажется ли фигура под ударом. И все ли остальные под охраной.'),
    s12('Перед ходом посмотри на все свои фигуры. Кого можно съесть? Кто стоит без защиты?'),
    s12('Есть идея хода? Не спеши! Сначала проверь, не съедят ли твою фигуру.'),
    s12('Хороший ход — это безопасный ход. Проверь, не попадёт ли фигура под удар на новом месте.'),
    // one sentence (blitz)
    s12('Прежде чем сходить, проверь: не съедят ли там нашу фигуру?'),
    s12('Пробегись глазами по своим фигурам: все ли под охраной?'),
    s12('Фигура на новом месте должна быть в безопасности — проверь это заранее.'),
    s12('Смотри, куда ставишь фигуру: не стреляет ли туда чужая пешка или фигура?'),
    s35('Второй вопрос — о безопасности. Что окажется под ударом, когда мы сходим? Нет ли у нас фигур без защиты?'),
    s35('Есть ход на примете? Представь, что ты играешь за соперника. Какие взятия и шахи у тебя появятся?'),
    s35('Обойди свои фигуры, как сторож с фонариком. Каждая ли под охраной? Никого не подставляем?'),
    s35('Ход может быть красивым, но опасным. Прежде чем сходить, посмотри, не съест ли соперник что-нибудь в ответ.'),
    s35('Думая о своём ходе, считай и ответ соперника. Если после него мы теряем фигуру, ищем другой ход.'),
    // one sentence (blitz)
    s35('Перед тем как сходить, представь ответ соперника: что он сможет съесть?'),
    s35('Проверь каждый ход на безопасность: сможет ли соперник что-нибудь съесть в ответ?'),
    s35('Хороший ход ничего не подставляет — ни ту фигуру, что ходит, ни остальные.'),
    s35('Ищем ход, после которого ни одна наша фигура не висит.'),
  ],
  'v3.mini.thinking.l3': [
    s12('Проверяй ходы по порядку, как лётчик перед полётом. Сначала шахи, потом — что можно съесть, потом — на кого напасть.'),
    s12('Проверяй по порядку: шахи, взятия, нападения. Так не пропустишь ни мат, ни подарок.'),
    s12('Три волшебных слова: шах, съесть, напасть. Проверь их за себя и за соперника.'),
    s12('Сильные ходы любят прятаться. Ищи их в трёх местах: шахи, взятия, нападения.'),
    s12('Проверка на трёх пальцах: шахи — раз, взятия — два, нападения — три. Так ни один подарок не спрячется.'),
    // one sentence (blitz)
    s12('Три проверки перед ходом: шахи, взятия и нападения.'),
    s12('Ищи сильный ход по порядку: сначала шах, потом взятие, потом нападение.'),
    s12('Шахи, взятия, нападения — проверяй их и за себя, и за соперника!'),
    s12('Как сыщик, проверь три места: шахи, взятия и нападения — свои и чужие.'),
    s35('Порядок проверки: шахи, взятия, угрозы. Для своих ходов и для ходов соперника.'),
    s35('Даже сильные игроки проверяют по списку: шахи, взятия, угрозы. Так ни одна тактика не проскочит мимо.'),
    s35('Сначала — все шахи, потом — все взятия, потом — угрозы. Это самые сильные ходы, их смотрят первыми.'),
    s35('Привычка чемпионов: перед ходом пройтись по списку — шахи, взятия, угрозы. И сделать это за себя и за соперника.'),
    s35('Не останавливайся на первом хорошем ходе. Проверь шахи, взятия и угрозы — вдруг найдётся ход сильнее.'),
    // one sentence (blitz)
    s35('Перед ходом пройди по списку: шахи, взятия, угрозы — за обе стороны.'),
    s35('Тактика чаще всего прячется в шахах, взятиях и угрозах — их проверяют первыми.'),
    s35('Смотри на доску глазами соперника: какие у него шахи, взятия и угрозы?'),
    s35('Порядок поиска простой: шахи, потом взятия, потом угрозы, а тихие ходы — в конце.'),
  ],

  // ───────────────────────── hanging [1–3], cue hanging ─────────────────────────
  // the hanging piece may be ours (danger) or the opponent's (treasure): no wording says whose it is
  'v3.mini.hanging.l1': [
    s12('Фигура без защиты висит, как яблоко на ветке. Сорвать её может любой, кто дотянется.'),
    s12('Если фигуру никто не защищает, говорят: она висит. Её можно съесть и ничего не отдать взамен.'),
    s12('Кто остался без охраны, того съедят даром. Взамен съесть будет некого.'),
    s12('Незащищённая фигура — как мячик без хозяина. Кто первый схватит, того и мяч!'),
    // one sentence (blitz)
    s12('Фигура без охраны висит — её могут съесть даром.'),
    s12('Без охраны фигура — лёгкая добыча: её забирают и ничего не отдают.'),
    s3('Висячая фигура — та, которую никто не защищает. Если на неё напали, её забирают бесплатно.'),
    s3('Первая цель для тактики — фигура без защиты. Любое нападение на неё сразу становится угрозой.'),
    s3('Больше всего подарков дают фигуры без защиты. Ищи такие и у себя, и у соперника.'),
    // one sentence (blitz)
    s3('Под ударом и без защиты — такую фигуру забирают даром.'),
    s3('Ищи висячие фигуры везде: у себя — чтобы спасти, у соперника — чтобы забрать.'),
  ],
  'v3.mini.hanging.l2': [
    s12('Как узнать, висит ли фигура? Найди всех, кто на неё смотрит: и нападающих, и охрану.'),
    s12('Смотри на фигуру с двух сторон. Кто на неё напал? Кто за неё заступится?'),
    s12('Проверь, у кого из наших нет охраны. Такую фигуру надо прикрыть или увести.'),
    s12('Нападают двое, а охраняет один? Тогда фигуру надо увести или защитить ещё раз.'),
    // one sentence (blitz)
    s12('Посчитай, кто на фигуру нападает и кто её охраняет.'),
    s12('Если нападающих больше, чем охранников, фигура в опасности.'),
    s3('Кто сильнее — нападение или охрана? Если нападающих больше, фигура в опасности.'),
    s3('Перед ходом пройдись взглядом по всем фигурам: кого атакуют и кто охраняет. Так висячие фигуры не пропустишь.'),
    s3('Защита должна быть настоящей. Проверь, не связана ли охраняющая фигура и не уведут ли её.'),
    // one sentence (blitz)
    s3('Сравни, сколько фигур нападает и сколько охраняет, — так видно, висит ли фигура.'),
    s3('Охранников должно быть не меньше, чем нападающих, иначе фигуру могут забрать.'),
  ],
  'v3.mini.hanging.l3': [
    s12('Бывает, фигура под охраной, а всё равно в беде. Так случается, когда на неё напала фигура дешевле.'),
    s12('Защита не спасает, если нападает кто-то дешевле. Пешка съест коня, мы съедим пешку — а конь дороже.'),
    s12('Если на ферзя напала пешка, защита не поможет. Отдавать ферзя за пешку никто не хочет — его уводят!'),
    s12('Запомни: дешёвая фигура, которая нападает на дорогую, опасна всегда. Даже если дорогую защищают.'),
    // one sentence (blitz)
    s12('Даже с охраной фигура в беде, если на неё напал кто-то дешевле.'),
    s12('Пешка, которая нападает на коня, опасна, даже когда коня охраняют.'),
    s3('Защищённая фигура тоже может висеть. Если её атакует фигура дешевле, размен выйдет невыгодным.'),
    s3('Ладья под ударом слона — беда, даже с защитой. Слон стоит три пешки, ладья — пять.'),
    s3('Важна не только защита, но и цена. Когда нападает кто-то дешевле, фигуру стоит увести.'),
    // one sentence (blitz)
    s3('Когда на дорогую фигуру нападает дешёвая, охрана уже не выручит.'),
    s3('Считай не только охрану, но и цену: дешёвый нападающий опасен всегда.'),
  ],

  // ───────────────────────── freeCapture [1–2], cue capture ─────────────────────────
  'v3.mini.freeCapture.l1': [
    all('Если фигуру соперника никто не охраняет, её можно съесть даром. Такое взятие называют бесплатным.'),
    all('Бесплатное взятие — как найти монетку на дороге. Берём и ничего не отдаём.'),
    all('Чужая фигура стоит одна, без охраны? Забираем её — такие подарки не пропускаем!'),
    all('Подарок в шахматах — фигура, которую можно забрать бесплатно. Соперник ничего не сможет вернуть.'),
  ],
  'v3.mini.freeCapture.l2': [
    all('Прежде чем брать, проверь: не съедят ли нашу фигуру в ответ? Если нет — смело бери.'),
    all('Перед взятием посмотри, кто охраняет чужую фигуру. Если охраны нет, подарок настоящий.'),
    all('Подарок надо проверить. Посмотри на клетку, куда встанет наша фигура: не стреляет ли по ней кто-то?'),
    all('Сначала посчитай ответ соперника. Съедим мы, а потом он нас — это уже размен, а не подарок.'),
  ],
  'v3.mini.freeCapture.l3': [
    all('Бывает, что подарок — ловушка. Посмотри, что откроется, когда наша фигура уйдёт со своего места.'),
    all('Хитрый соперник иногда оставляет фигуру нарочно. Прежде чем брать, проверь: нет ли у него мата или сильного удара?'),
    all('Иногда фигура, съевшая подарок, сама попадает в западню. Проверь, сможет ли она потом убежать.'),
    all('Подарок может быть приманкой. Если его взять, не останется ли без защиты другая наша фигура?'),
  ],

  // ───────────────────────── checkEscape [1–2], cue king ─────────────────────────
  'v3.mini.checkEscape.l1': [
    all('От шаха есть три спасения. Уйти королём, закрыться другой фигурой или съесть фигуру, которая даёт шах.'),
    all('Против шаха есть три двери: уйти, закрыться, съесть. Хотя бы одна обычно открыта.'),
    all('Шах — ещё не беда, если знаешь три выхода. Отойти королём, заслониться фигурой или съесть нападающего.'),
    all('Королю шах? Не пугайся! Можно уйти, можно закрыться, а можно съесть обидчика.'),
    // one sentence (blitz)
    all('От шаха спасают три хода: уйти, закрыться или съесть того, кто шахует.'),
    all('При шахе вспоминай три выхода: уйти, закрыться, съесть.'),
  ],
  'v3.mini.checkEscape.l2': [
    all('Спасений бывает несколько — выбирай самое выгодное. То, после которого все фигуры целы.'),
    all('Сначала посмотри, можно ли бесплатно съесть того, кто шахует. Часто это самый выгодный способ.'),
    all('Закрыться от шаха можно, но проверь: не съедят ли того, кто закрыл. Иначе спасение выйдет дорогим.'),
    all('Уходя королём, выбирай клетку поспокойнее. Подальше от чужих ферзя и ладей.'),
    // one sentence (blitz)
    all('Не хватайся за первое спасение от шаха — поищи то, где все фигуры целы.'),
    all('Лучшее спасение от шаха — то, после которого мы ничего не теряем.'),
  ],
  'v3.mini.checkEscape.l3': [
    all('Бывает спасение с сюрпризом: закрылись от шаха — и сами напали! Такие ходы очень сильные.'),
    all('Иногда, спасаясь от шаха, можно напасть на чужую фигуру. Ищи такие ходы!'),
    all('Самое приятное спасение — когда соперник после него сам должен защищаться. Поищи такое, прежде чем просто убегать.'),
    all('Даже сам король, уходя от шаха, может напасть на чужую фигуру. Убежал — и сразу угроза!'),
    // one sentence (blitz)
    all('Здорово, когда спасение от шаха сразу нападает на чужую фигуру.'),
    all('Закрываясь от шаха, поищи ход, который ещё и нападает.'),
  ],

  // ───────────────────────── mateInOne [1–2], cue king ─────────────────────────
  'v3.mini.mateInOne.l1': [
    all('Мат — это шах, от которого не спастись. Кто поставил мат, тот и победил!'),
    all('Когда королю шах и спастись никак нельзя, это мат. Партия сразу заканчивается.'),
    all('Мат — главная цель в шахматах. Король под шахом, и нет ни одного спасения.'),
    all('Шах можно отбить, а мат — нет. Мат ставят один раз, и игра окончена.'),
    // one sentence (blitz)
    all('Если король под шахом и спасения нет, партия окончена — это мат.'),
    all('Кто первым поставит мат, тот и выиграл, даже если у него меньше фигур.'),
  ],
  'v3.mini.mateInOne.l2': [
    all('Чтобы найти мат, проверь каждый шах. Может ли король уйти? Можно ли закрыться или съесть?'),
    all('Нашли шах? Проверь три двери: уйти, закрыться, съесть. Если все закрыты — это мат!'),
    all('Смотри все шахи по очереди — и свои, и соперника. Для каждого ищи спасение. Нет спасения — значит, мат.'),
    all('Уйти нельзя, закрыться нельзя, съесть нельзя? Тогда это мат! Так и проверяй каждый шах.'),
    // one sentence (blitz)
    all('Если от шаха нельзя ни уйти, ни закрыться, ни съесть обидчика, это мат.'),
    all('Проверяй каждый шах: если от него нет спасения, это мат.'),
  ],
  'v3.mini.mateInOne.l3': [
    all('Мат часто ставят королю, которого заперли свои же фигуры. Ему некуда шагнуть.'),
    all('Если король соперника окружён своими пешками, ищи мат! Иногда хватает одного шаха.'),
    all('Смотри, куда может шагнуть чужой король. Если все клетки рядом заняты его же фигурами, мат может быть близко.'),
    all('Самые красивые маты — когда король сам себя запер. Свои фигуры стоят вокруг и не дают ему уйти.'),
    // one sentence (blitz)
    all('Король, запертый своими фигурами, — лёгкая добыча для мата.'),
    all('Когда свои фигуры обступили короля, одного шаха может хватить для мата.'),
  ],

  // ───────────────────────── scholarsMate [1–3], cue weak / threat ─────────────────────────
  'v3.mini.scholarsMate.l1': [
    s12('Есть быстрая ловушка: ферзь и слон вместе нападают на пешку рядом с королём. Её защищает только сам король.'),
    s12('Ферзь и слон могут вдвоём ударить в одну пешку возле короля. Если её взять, может получиться мат.'),
    s12('В начале партии у короля есть слабое место — пешка, которую охраняет только он сам. Туда любят бить ферзь и слон.'),
    // one sentence (blitz)
    s12('Ферзь со слоном вдвоём бьют в пешку у короля — так бывает быстрый мат.'),
    s12('Есть пешка, которую охраняет лишь король, — в неё и метят ферзь со слоном.'),
    at(2, 2)('Детский мат — это когда ферзь и слон вместе бьют в слабую пешку у короля. Мат всего за четыре хода!'),
    s3('Детский мат — удар ферзя и слона в слабую пешку рядом с королём. Её охраняет только сам король.'),
    s3('Слабая пешка рядом с королём в начале партии — любимая цель ферзя и слона. Так получается детский мат.'),
    s3('Один из самых быстрых матов — детский. Ферзь и слон вдвоём атакуют пешку, которую защищает только король.'),
    // one sentence (blitz)
    s3('Удар ферзя и слона в слабую пешку у короля называют детским матом.'),
    s3('Когда ферзь и слон смотрят в одну пешку у короля, жди детского мата.'),
  ],
  'v3.mini.scholarsMate.l2': [
    s12('Защититься просто: закрой дорогу ферзю конём или пешкой. Мат сразу пропадёт.'),
    s12('Если ферзь и слон целятся в пешку у короля, её надо защитить. Помогут конь, пешка или даже свой ферзь.'),
    s12('Против такой ловушки хватит одного хода: конь или пешка закрывают удар. Главное — вовремя заметить.'),
    // one sentence (blitz)
    s12('Чтобы не попасться на быстрый мат, прикрой слабую пешку у короля ещё раз.'),
    s12('Вовремя заметить угрозу ферзя и слона — значит наполовину её отбить.'),
    at(2, 2)('От детского мата легко спастись, если увидеть его заранее. Закрой слабую пешку конём или пешкой.'),
    s3('Против детского мата выводи коня и закрывай удар ферзя. Так мы и защищаемся, и фигуру выводим.'),
    s3('Слабую пешку прикрывают конём, пешкой или своим ферзём. Любой из этих ходов отбивает угрозу.'),
    s3('Главное — вовремя увидеть угрозу. Один спокойный ход конём или пешкой, и детский мат не проходит.'),
    // one sentence (blitz)
    s3('От детского мата спасает один спокойный ход: закрыться или защитить слабую пешку.'),
    s3('Детский мат не страшен тому, кто вовремя прикрыл слабую пешку у короля.'),
  ],
  'v3.mini.scholarsMate.l3': [
    s12('Отобьём угрозу — и сами в атаку! Нападай на ферзя соперника конями и слонами: ему придётся убегать.'),
    s12('Если ферзь соперника рано прибежал к нашему королю, его можно прогнать. Каждый такой ход будит новую нашу фигуру.'),
    s12('Ферзь, который рано вышел за матом, — лёгкая мишень. Прогони его и выведи свои фигуры.'),
    // one sentence (blitz)
    s12('Ферзь соперника прибежал рано — значит, его можно гонять нашими фигурами.'),
    s12('Отобьём мат — и можно гонять чужого ферзя, выводя свои фигуры.'),
    at(2, 2)('Кто пытается поставить детский мат, часто сам попадает в беду. Его ферзь вышел рано, а мы гоняем его своими фигурами.'),
    s3('Неудачная попытка детского мата — наш шанс. Ранний ферзь соперника становится мишенью для наших фигур.'),
    s3('Не удастся детский мат — наступит наша очередь. Гони ферзя соперника и выводи свои фигуры.'),
    s3('Ферзь, который рано ушёл в атаку, потом долго убегает. Каждое нападение на него — наш лишний ход развития.'),
    // one sentence (blitz)
    s3('Ранний ферзь соперника — удобная мишень для наших коней и слонов.'),
    s3('Отбив детский мат, гони ферзя соперника — так фигуры выходят быстрее.'),
  ],

  // ───────────────────────── backRank [2–5], cue king ─────────────────────────
  'v3.mini.backRank.l1': [
    s2('Король спрятался за три пешки, а они его заперли. Если чужая ладья ворвётся на последний ряд, бежать некуда.'),
    s2('Мат по последнему ряду: свои пешки не выпускают короля вперёд. Хватит одной ладьи или ферзя.'),
    s2('Бывает мат, который помогают поставить свои же пешки. Они заперли короля, а ладья соперника дала шах сзади.'),
    // one sentence (blitz)
    s2('За тремя пешками король может оказаться в ловушке при шахе по последнему ряду.'),
    s2('Свои пешки могут запереть короля, и тогда хватит одной ладьи для мата.'),
    s35('Мат по последнему ряду — частая беда. Король заперт своими пешками, а ладья или ферзь дают шах сзади.'),
    s35('Пешки перед рокированным королём его охраняют, но могут и запереть. Если на последний ряд ворвётся ладья, это может быть мат.'),
    s35('Король за тремя целыми пешками не может шагнуть вперёд. Тогда шах по последнему ряду легко становится матом.'),
    // one sentence (blitz)
    s35('Мат по последнему ряду ставят королю, которого заперли его же пешки.'),
    s35('Когда королю некуда шагнуть с последнего ряда, шах по нему может стать матом.'),
    s45('Последняя горизонталь — слабое место рокированного короля. Если её никто не охраняет, ладья соперника может поставить мат.'),
  ],
  'v3.mini.backRank.l2': [
    s2('Королю нужна форточка — запасная клетка для побега. Сдвинь одну пешку перед ним на шаг вперёд.'),
    s2('Как не попасться? Одна пешка перед королём шагает вперёд, и у него появляется выход.'),
    s2('Когда будет спокойный ход, открой королю окошко. Тогда шах по последнему ряду уже не страшен.'),
    // one sentence (blitz)
    s2('Шаг одной пешкой перед королём — и у него появляется форточка.'),
    s2('Запасная клетка для короля спасает от мата по последнему ряду.'),
    s35('Лекарство простое — форточка. Один ход пешкой перед королём, и ему есть куда уйти от шаха.'),
    s35('В спокойный момент дай королю форточку. Обычно двигают крайнюю пешку — так стенка почти не слабеет.'),
    s35('Форточка стоит всего один ход, а спасает от мата. Сделай её, пока соперник не угрожает.'),
    // one sentence (blitz)
    s35('Одна форточка у короля — и шах по последнему ряду уже не так страшен.'),
    s35('Форточка — самый простой способ спасти короля от мата по последнему ряду.'),
  ],
  'v3.mini.backRank.l3': [
    s2('Если последний ряд охраняет ладья, не уводи её. Без неё король останется один.'),
    s2('Смотри, кто сторожит последний ряд. Эту фигуру лучше не уводить, пока у короля нет форточки.'),
    s2('Ладья на последнем ряду — сторож короля. Пока нет форточки, сторожа не отпускаем.'),
    // one sentence (blitz)
    s2('Ладья, которая сторожит последний ряд, пусть остаётся на посту.'),
    s2('Пока у короля нет форточки, не уводи того, кто охраняет последний ряд.'),
    s35('У последнего ряда есть сторож — обычно ладья. Если она уйдёт или её разменяют, король останется без охраны.'),
    s35('Соперник может выманить сторожа последнего ряда жертвой. Проверь, не бросит ли наша ладья свой пост.'),
    s35('Последний ряд охраняет ладья? Не уводи её в атаку, пока у короля нет форточки.'),
    // one sentence (blitz)
    s35('Сторож последнего ряда — важная фигура: её не меняют и не уводят без нужды.'),
    s35('Одна ладья на последнем ряду — хрупкая охрана: её легко выманить или разменять.'),
    s45('Сторожа последней горизонтали можно отвлечь шахом или жертвой. Прежде чем уводить ладью, подумай, кто охранит короля.'),
  ],

  // ───────────────────────── fork [2–5], cue attacks ─────────────────────────
  // the fork may be ours (treasure) or the opponent's (danger): the words say who suffers, not «соперник»
  'v3.mini.fork.l1': [
    s2('Если одна фигура нападает сразу на две, это вилка. Часто одну из двух приходится отдать.'),
    s2('Почему вилка? Одна фигура накалывает сразу две, как вилка — две ягоды.'),
    s2('Двойной удар называют вилкой. Две фигуры под ударом, а спасти можно только одну.'),
    // one sentence (blitz)
    s2('Одна фигура бьёт сразу по двум — это и есть вилка.'),
    s2('Вилка колет сразу две фигуры, и обе спасти обычно не получается.'),
    s35('Вилка — двойное нападение одной фигурой. Из двух атакованных фигур обычно удаётся спасти только одну.'),
    s35('Вилка — главный приём тактики: один ход, два нападения. Защитить обе цели сразу получается редко.'),
    s35('Вилку умеют ставить все фигуры, даже пешка и король. Главное — напасть на две цели сразу.'),
    // one sentence (blitz)
    s35('Один ход — и сразу два нападения: так работает вилка.'),
    s35('При вилке ход один, а спасать надо сразу две фигуры.'),
  ],
  'v3.mini.fork.l2': [
    s2('Больше всех вилки любит конь. Он прыгает хитро, и его вилку трудно заметить.'),
    s2('Хочешь найти вилку? Посмотри, куда может прыгнуть конь. Не нападёт ли он оттуда на две фигуры?'),
    s2('Конь — мастер вилок. Проверяй прыжки всех коней — и своих, и чужих.'),
    // one sentence (blitz)
    s2('Конь — главный любитель вилок, следи за его прыжками.'),
    s2('Перед ходом глянь на коней: нет ли у них прыжка на две фигуры сразу?'),
    s35('Чаще всех вилки ставит конь: он бьёт во все стороны и прыгает через фигуры. Его удар легко проглядеть.'),
    s35('Чтобы найти вилку, ищи удобные цели: короля, ферзя, фигуры без защиты. Потом смотри, какая наша фигура нападёт на две сразу.'),
    s35('Следи и за конём соперника. Если он может прыгнуть туда, откуда бьёт две наши фигуры, это опасно.'),
    // one sentence (blitz)
    s35('Вилку чаще всех ставит конь, поэтому прыжки коней проверяй в первую очередь.'),
    s35('Следи за конями — и своими, и чужими: их вилки легче всего проглядеть.'),
  ],
  'v3.mini.fork.l3': [
    s2('Самая сильная вилка — с шахом. Король обязан спасаться, и вторая фигура пропадает.'),
    s2('Если вилка нападает на короля, выбора нет. Сначала спасают короля, а вторую фигуру забирают.'),
    s2('Ищи вилку с шахом! От неё не отвертеться: сначала надо спасать короля.'),
    // one sentence (blitz)
    s2('Вилка с шахом страшнее всех: сначала спасают короля, и вторая фигура обычно пропадает.'),
    s2('Самые страшные вилки — с шахом: король спасается, а другую фигуру обычно забирают.'),
    s35('Опаснее всего вилка с шахом. Король обязан спасаться, и защитить вторую фигуру уже не успеть.'),
    s35('Если вилка бьёт по королю и ферзю, ферзя обычно не спасти.'),
    s35('Иногда фигуру отдают, чтобы потом поставить вилку с шахом. Чужой король встаёт на нужную клетку — и удар!'),
    // one sentence (blitz)
    s35('Шах с вилкой не оставляет выбора: король спасается, а вторая цель остаётся под ударом.'),
    s35('Ищи вилки с шахом и за себя, и за соперника — они сильнее всех.'),
  ],

  // ───────────────────────── pin [3–5], cue line ─────────────────────────
  'v3.mini.pin.l1': [
    all('Связка — это когда фигура не может уйти с линии. За ней стоит кто-то дороже: король, ферзь или ладья.'),
    all('Связанная фигура как приклеенная. Уйдёт — и откроет удар по тому, кто стоит сзади.'),
    all('Если за фигурой стоит король, уходить ей запрещено правилами. Это самая крепкая связка.'),
    all('Связку ставят слон, ладья и ферзь — фигуры, которые бьют издалека. Передняя фигура прикрывает заднюю.'),
  ],
  'v3.mini.pin.l2': [
    all('Связанную фигуру хорошо атаковать ещё раз. Особенно пешкой — ведь убежать фигура не может.'),
    all('Связанная фигура — удобная мишень. Приведи к ней ещё одного нападающего, и защиты может не хватить.'),
    all('Нашли связку? Не спеши меняться. Сначала посмотри, чем ещё можно напасть на приклеенную фигуру.'),
    all('Связанная фигура плохо охраняет других. Проверь, кого она сторожит, — там может ждать подарок.'),
  ],
  'v3.mini.pin.l3': [
    all('Если наша фигура связана, связку можно разорвать. Закрой линию другой фигурой или уведи того, кто сзади.'),
    all('Из связки есть выходы. Можно поставить на линию ещё одну фигуру или отвести короля в сторону.'),
    all('Связку можно разорвать и нападением. Прогони фигуру, которая связала, — и наша освободится.'),
    all('Бывает ложная связка: передняя фигура уходит с угрозой, и соперник не успевает взять заднюю.'),
  ],

  // ───────────────────────── skewer [3–5], cue line ─────────────────────────
  'v3.mini.skewer.l1': [
    all('Сквозной удар похож на связку наоборот. Впереди стоит дорогая фигура, а за ней — другая.'),
    all('При сквозном ударе мы нападаем на дорогую фигуру. А за ней на той же линии прячется ещё одна.'),
    all('Сквозной удар бьёт насквозь: по королю или ферзю и дальше, по фигуре за ним.'),
  ],
  'v3.mini.skewer.l2': [
    all('Дорогая фигура обязана уйти от удара. И тогда мы забираем ту, что стояла за ней.'),
    all('Чаще всего сквозной удар — это шах. Король отходит, и под ударом оказывается фигура за ним.'),
    all('Король или ферзь убегает, а наша фигура бьёт дальше по той же линии. Так и выигрываем материал.'),
  ],
  'v3.mini.skewer.l3': [
    all('Не ставь короля и ферзя на одной линии. Соперник может ударить по ним насквозь.'),
    all('Проверяй свои фигуры: не стоят ли король и ладья на одной линии или диагонали? Это цель для сквозного удара.'),
    all('Сквозной удар легко получить самому. Держи дорогие фигуры подальше от одной линии с королём.'),
  ],

  // ───────────────────────── discovered [3–5], cue line ─────────────────────────
  'v3.mini.discovered.l1': [
    all('Бывает хитрый удар: две наши фигуры стоят на одной линии. Передняя отходит — и задняя вдруг нападает.'),
    all('Представь, что фигура прячет за собой ладью или слона. Стоит ей отойти — и открывается удар.'),
    all('Иногда нападает не та фигура, что сходила, а та, что стояла за ней. Передняя ушла — дорога открылась.'),
    s45('Вскрытое нападение: передняя фигура отходит и открывает дорогу задней. Удар приходит, откуда не ждали.'),
  ],
  'v3.mini.discovered.l2': [
    all('Самое интересное — передняя фигура тоже может напасть. Тогда у соперника сразу две беды, а справиться можно только с одной.'),
    all('Отходя, передняя фигура может дать шах или напасть сама. Выходит двойной удар!'),
    all('Ищи, куда передняя фигура может отойти с угрозой. Тогда задняя ударит, а соперник не успеет защититься.'),
    s45('Сила вскрытого нападения — в двух ударах сразу. Одна угроза от передней фигуры, другая — от задней.'),
  ],
  'v3.mini.discovered.l3': [
    all('Самое опасное — когда при отходе открывается шах. Королю надо спасаться, а передняя фигура делает что хочет.'),
    all('Если отход открывает шах королю, передняя фигура свободна. Она может съесть даже ферзя.'),
    all('Когда задняя фигура даёт шах, соперник занят королём. А передняя тем временем забирает добычу.'),
    s45('Вскрытый шах — самый опасный. Передняя фигура может встать почти куда угодно, даже под удар.'),
  ],

  // ───────────────────────── removeDefender [3–5], cue capture ─────────────────────────
  'v3.mini.removeDefender.l1': [
    all('Фигуру соперника охраняют? Сначала убери того, кто охраняет. Тогда она останется одна.'),
    all('Невыгодно брать фигуру, потому что её защищают? Съешь сначала охранника — и забирай.'),
    all('Бывает, что фигуру спасает один-единственный охранник. Уберём его — и она станет беззащитной.'),
    s45('Уничтожение защитника: съедаем фигуру, которая охраняет цель. Потом забираем саму цель.'),
  ],
  'v3.mini.removeDefender.l2': [
    all('Прежде чем брать, посчитай, кто кого охраняет. Часто одна фигура сторожит сразу двух.'),
    all('Смотри на цепочку охраны: кто за кого отвечает. Самое слабое звено — фигура, у которой много работы.'),
    all('Найди цель, которую охраняет только одна фигура. Убери эту охрану — и цель повиснет.'),
    s45('Сначала посчитай защитников и нападающих. Если защитник один, подумай, как его убрать.'),
  ],
  'v3.mini.removeDefender.l3': [
    all('Охранника не обязательно съедать. Можно прогнать его или заставить уйти шахом.'),
    all('Охрану можно увести жертвой. Фигура соперника съест приманку и бросит свой пост.'),
    all('Если охранника нельзя съесть, нападай на него. Уйдёт — и охраняемая фигура останется одна.'),
    s45('Защитника можно отвлечь: заставить уйти с поста шахом или жертвой. Пост опустеет — и цель наша.'),
  ],

  // ───────────────────────── trapped [3–5], cue piece ─────────────────────────
  'v3.mini.trapped.l1': [
    all('Фигуру можно поймать, если ей некуда уйти. Сначала закрой ей все выходы, потом нападай.'),
    all('Ловить фигуру — как бабочку сачком. Сначала закрываем ей все пути, потом забираем.'),
    all('Фигура в ловушке — это фигура без безопасных клеток. Её можно спокойно окружить и забрать.'),
  ],
  'v3.mini.trapped.l2': [
    all('Фигуре у края доски трудно убежать: половина дорог закрыта. Особенно часто ловят коней и слонов.'),
    all('Чаще всего попадаются фигуры, которые забрались далеко. У края доски у них мало клеток для отступления.'),
    all('Конь на краю доски смотрит на мало клеток. Закрой их — и коню некуда бежать.'),
  ],
  'v3.mini.trapped.l3': [
    all('Бывает, жадный слон съест дальнюю пешку — и выбраться уже не может. Не заводи фигуры в тупик.'),
    all('Прежде чем залезть в лагерь соперника, проверь путь назад. Иначе фигуру могут запереть.'),
    all('Частая ловушка — лакомая пешка далеко от дома. Съели её — а вернуться уже нельзя.'),
  ],

  // ───────────────────────── pieceValues [1–2], no cue ─────────────────────────
  'v3.mini.pieceValues.l1': [
    all('У каждой фигуры своя цена, и считают её в пешках. Конь и слон — по три, ладья — пять, ферзь — девять.'),
    all('Фигуры стоят по-разному. Ферзь — как девять пешек, ладья — пять, конь и слон — по три.'),
    all('Кто дороже всех? Ферзь — целых девять пешек! Ладья — пять, конь и слон — по три.'),
    all('Самая дешёвая фигура — пешка, а короля вообще не меняют. Конь и слон стоят по три пешки, ладья — пять, ферзь — девять.'),
  ],
  'v3.mini.pieceValues.l2': [
    all('Меняться выгодно, когда получаешь не меньше, чем отдаёшь. Отдать коня за ладью — хорошо, ферзя за коня — плохо.'),
    all('Размен — как обмен наклейками: смотри, что отдаёшь и что получаешь. Отдать три и получить пять — выгодно!'),
    all('Конь за коня — честный размен. А отдавать ладью за слона невыгодно: пять больше трёх.'),
    all('Прежде чем съесть, сравни цены. Если в ответ съедят фигуру дороже, такой размен нам не нужен.'),
  ],
  'v3.mini.pieceValues.l3': [
    all('Цена — это ещё не всё. Конь и слон вместе обычно сильнее одной ладьи.'),
    all('Бывает, что пешка важнее коня: например, если она вот-вот станет ферзём.'),
    all('Слон и конь стоят почти одинаково. А вот два слона вместе — особенно сильная пара.'),
    all('Две фигуры по три обычно сильнее ладьи. Поэтому отдавать коня и слона за ладью невыгодно.'),
  ],

  // ───────────────────────── tradeWhenAhead [3–5], cue capture ─────────────────────────
  'v3.mini.tradeWhenAhead.l1': [
    all('Когда мы впереди по материалу, размены помогают нам. Меняемся — и перевес становится заметнее.'),
    all('Ведём по материалу? Тогда меняй фигуры. Чем меньше их на доске, тем сильнее наш лишний материал.'),
    all('Простое правило: впереди по материалу — меняйся. Лишняя фигура сильнее, когда вокруг пусто.'),
  ],
  'v3.mini.tradeWhenAhead.l2': [
    all('Чем меньше фигур на доске, тем меньше у соперника хитростей. Перевес легче довести до победы.'),
    all('Лишняя ладья на пустой доске — это почти победа. А среди многих фигур соперник ещё может нас запутать.'),
    all('Когда фигур мало, сопернику труднее атаковать. А наша лишняя фигура спокойно помогает пешкам.'),
  ],
  'v3.mini.tradeWhenAhead.l3': [
    all('Меняй фигуры, а пешки береги. Именно пешка может стать новым ферзём.'),
    all('Если разменять все пешки, лишней фигуры может не хватить для мата. Поэтому меняем фигуры, а пешки оставляем.'),
    all('Размен пешек помогает тому, кто отстаёт. Один лишний конь без пешек мат не поставит.'),
  ],

  // ───────────────────────── openFile [3–5], cue line ─────────────────────────
  'v3.mini.openFile.l1': [
    all('Открытая линия — вертикаль, на которой нет пешек. Это дорога для ладьи.'),
    all('Ладья любит простор. Ей нравятся открытые линии — там, где пешки не мешают.'),
    all('Вот эта линия свободна от пешек. Ладья на ней смотрит через всю доску.'),
    all('За своими пешками ладья скучает. На открытой линии она оживает.'),
  ],
  'v3.mini.openFile.l2': [
    all('С открытой линии ладья достаёт до самого конца доски. Оттуда она может ворваться в лагерь соперника.'),
    all('По открытой линии ладья доберётся до пешек соперника и до его короля.'),
    all('Кто первым займёт открытую линию, тот на ней и хозяин. Поэтому за линии идёт настоящая гонка.'),
  ],
  'v3.mini.openFile.l3': [
    all('Две ладьи на одной открытой линии — сила вдвое больше. Одна поддерживает другую.'),
    all('Поставь ладьи друг за другом на открытой линии. Такую пару остановить трудно.'),
    all('Одну ладью на открытой линии соперник ещё может остановить. Две ладьи друг за другом — уже с трудом.'),
  ],

  // ───────────────────────── connectRooks [3–5], cue line ─────────────────────────
  'v3.mini.connectRooks.l1': [
    all('Ладьи увидели друг друга — между ними нет фигур. Это знак: дебют закончен.'),
    all('Кони, слоны и ферзь вышли, король спрятан — и ладьи соединились. Дебют сделан!'),
    all('Когда ладьи соединились, фигуры обычно уже вышли. Пора переходить к плану.'),
  ],
  'v3.mini.connectRooks.l2': [
    all('Соединённые ладьи стоят друг за друга горой. Одну съедят — другая заберёт обидчика.'),
    all('Вместе ладьи сильнее: они охраняют друг друга и свой последний ряд.'),
    all('Соединённые ладьи легко переходят на любую линию. Туда, где открывается дорога.'),
  ],
  'v3.mini.connectRooks.l3': [
    all('Ладьи соединились — теперь найди им открытую линию. Там они заработают в полную силу.'),
    all('Следующий шаг — открытая линия. Поставь туда ладью, а вторую — за ней.'),
    all('Соединённые ладьи ещё ждут своей работы. Им нужна линия без пешек, где они пойдут вперёд.'),
  ],

  // ───────────────────────── kingActive [2–5], cue king / center ─────────────────────────
  'v3.mini.kingActive.l1': [
    s2('Когда фигур на доске мало, король перестаёт прятаться. Он становится сильным бойцом.'),
    s2('Мало фигур на доске? Пора будить короля! Теперь он такой же боец, как все.'),
    s2('Когда у соперника нет ферзя, королю уже не так страшно. Он выходит из домика и помогает своим.'),
    s35('Фигуры разменялись — и король осмелел. Теперь он сам идёт в бой.'),
    s35('В конце партии король — не беглец, а боец. Он стоит почти как конь или слон.'),
    s35('Прятать короля в углу, когда фигур мало, — значит играть без одной фигуры. Теперь он сильный боец.'),
    s5('Эндшпиль — это когда короли выходят в бой. Активный король часто решает партию.'),
  ],
  'v3.mini.kingActive.l2': [
    s2('Веди короля к центру: оттуда он быстро добежит куда нужно. А потом — к пешкам.'),
    s2('Король идёт в центр и помогает пешкам. Он может защищать своих и есть чужих.'),
    s2('Середина доски — любимое место короля в конце партии. Оттуда до любой пешки рукой подать.'),
    s35('Первым делом веди короля к центру. Оттуда он успеет и к своим пешкам, и к чужим.'),
    s35('Король в центре держит много клеток. Он мешает чужому королю и помогает своим пешкам.'),
    s35('Каждый шаг короля к центру делает его сильнее. Кто первым приведёт короля, тот и командует.'),
  ],
  'v3.mini.kingActive.l3': [
    s2('Свою пешку король может проводить в ферзи. Он идёт рядом и не пускает чужого короля.'),
    s2('Одинокую пешку чужой король часто ловит. А с нашим королём рядом она нередко прорвётся в ферзи.'),
    s2('Король — лучший друг пешки. Он идёт впереди и расчищает ей дорогу к превращению.'),
    s35('Король идёт впереди своей пешки и отталкивает чужого короля. Так пешка проходит в ферзи.'),
    s35('Своя пешка и король рядом — сильная команда. Король защищает её на каждом шагу.'),
    s35('Чужой король мешает нашей пешке? Наш король встанет рядом и оттеснит его.'),
  ],

  // ───────────────────────── passedPawn [3–5], cue path ─────────────────────────
  'v3.mini.passedPawn.l1': [
    all('Бывает пешка, которой не мешает ни одна чужая пешка. Ни впереди, ни на соседних линиях.'),
    all('Перед пешкой нет чужих пешек, и сбоку её никто не собьёт? Значит, пешки её уже не остановят.'),
    all('Пешка со свободной дорогой — будущий ферзь. Ни одна чужая пешка не может её остановить.'),
    s5('Проходная пешка — та, которой не мешают пешки соперника. У неё открыта дорога к превращению.'),
  ],
  'v3.mini.passedPawn.l2': [
    all('Свободную пешку надо двигать вперёд. Чем ближе она к последнему ряду, тем опаснее.'),
    all('Веди свободную пешку в ферзи. Король и ладья пусть помогают и расчищают ей путь.'),
    all('Каждый шаг свободной пешки пугает соперника. Ему приходится тратить фигуры, чтобы её остановить.'),
    s5('Проходные пешки должны двигаться. Веди её вперёд, а король и фигуры пусть помогают.'),
  ],
  'v3.mini.passedPawn.l3': [
    all('Чужую свободную пешку останавливай фигурой, которая встанет прямо перед ней. Упрётся — и дальше не пройдёт.'),
    all('Не гонись за чужой пешкой сзади — встань у неё на пути. Фигура впереди пешки её не пропустит.'),
    all('Пешка ходит только вперёд. Поставь фигуру прямо перед ней — и пешка застынет на месте.'),
    s5('Чужую проходную останавливай фигурой прямо перед ней. Такая блокада надёжнее погони.'),
  ],

  // ───────────────────────── mateTechnique [2–5], cue king ─────────────────────────
  'v3.mini.mateTechnique.l1': [
    s2('У соперника остался один король? Значит, пора ставить мат. Сначала загоняем его к краю доски.'),
    s2('Шаг за шагом прижимаем одинокого короля к краю. Там ему и будет мат.'),
    s2('Мат одинокому королю ставят у края. Наша задача — туда его и загнать.'),
    s35('Против одинокого короля план простой: прижать его к краю доски. Там и ставится мат.'),
    s35('Одинокий король ещё бегает, но уже не опасен. Загоняем его к краю, где мат ставить легче всего.'),
    s35('Сначала — край доски, потом — мат. Так побеждают одинокого короля.'),
  ],
  'v3.mini.mateTechnique.l2': [
    s2('Строим вокруг короля коробочку. С каждым ходом она становится всё меньше.'),
    s2('Отнимай у короля клетки по одной. Пусть коробочка сжимается, пока он не упрётся в край.'),
    s2('Ферзь или ладья ставят забор, и король не может его перейти. Потом забор двигаем ближе.'),
    s35('Коробочка: ферзь или ладья отрезают королю кусок доски. Каждым ходом коробочку уменьшаем.'),
    s35('Не нужно всё время давать шах. Отнимай клетки, чтобы коробочка становилась теснее.'),
    s35('Шахи без плана только гоняют короля по доске. Спокойные ходы, которые отнимают клетки, работают надёжнее.'),
  ],
  'v3.mini.mateTechnique.l3': [
    s2('Один ферзь или одна ладья мат не поставят. Им нужен помощник — наш король.'),
    s2('Когда чужой король у края, подводи своего короля. Вдвоём с ферзём или ладьёй они и ставят мат.'),
    s2('Наш король — важный помощник в мате. Он встаёт рядом и отнимает у соперника последние клетки.'),
    s35('Ферзь в одиночку мат не поставит — нужен наш король. Подводи его ближе, пока ферзь держит коробочку.'),
    s35('С ладьёй мат обычно ставят, когда короли стоят друг напротив друга. Тогда ладья даёт шах по краю.'),
    s35('Последний шаг — привести своего короля. Он отнимает клетки, а ферзь или ладья ставит мат.'),
  ],

  // ───────────────────────── stalemate [2–5], cue king ─────────────────────────
  'v3.mini.stalemate.l1': [
    s2('Пат — это когда у соперника нет ни одного хода, но и шаха нет. Тогда ничья!'),
    s2('Осторожно, пат! Если королю не шах, но ходить ему некуда, партия заканчивается вничью.'),
    s2('Король не под шахом, но ходить ему некуда? Это пат, а не мат. И это ничья, а не победа.'),
    s35('Пат — ничья: у соперника нет ходов, хотя шаха нет. Даже с лишним ферзём можно не выиграть.'),
    s35('Главная ловушка для того, кто выигрывает, — пат. Нет шаха и нет ходов — и победа превращается в ничью.'),
    s35('Когда у соперника нет ни одного хода и ему не шах, это пат. Партия кончается вничью.'),
  ],
  'v3.mini.stalemate.l2': [
    s2('Чтобы не было пата, оставляй чужому королю хотя бы одну клетку. Пусть ему будет куда шагнуть.'),
    s2('Пока мат не поставлен, королю соперника нужен хоть один ход. Иначе будет ничья.'),
    s2('Не запирай короля слишком рано. Пусть у него остаётся ход, пока мы готовим мат.'),
    s35('Прижимая короля, всегда оставляй ему хоть один ход. Последнюю клетку отнимаем только матом.'),
    s35('Пат чаще всего получается, когда ферзь подходит слишком близко. Оставь королю соперника свободную клетку.'),
    s35('Сжимай коробочку осторожно: у короля должен оставаться ход. Иначе вместо мата выйдет пат.'),
  ],
  'v3.mini.stalemate.l3': [
    s2('Перед каждым ходом спроси себя: будет ли у соперника хоть один ход? Если нет, а шаха нет — это пат.'),
    s2('Смотри не только на короля, но и на пешки соперника. Если пешка может ходить, пата нет.'),
    s2('Прежде чем сходить, найди для чужого короля хоть один ход. Есть такой ход — можно ходить смело.'),
    s35('Перед каждым ходом проверяй: останется ли у соперника хоть один ход? Считай и короля, и пешки.'),
    s35('Когда выигрываешь, проверка на пат обязательна. Один поспешный ход — и вместо победы ничья.'),
    s35('Проигрывающий соперник иногда сам ищет пат: отдаёт фигуры, чтобы остаться без ходов.'),
  ],
};
